import { OpenAPI } from "@propertelligent/client-api";
import { UserLoginData } from "../../common/charts/Enums";
import { EmailProviderType } from "../../../utils/emailProviderType";
import { NotificationType, PubSub, TopicsEnum } from "../../../utils/pubsub";
export { EmailProviderType };

/**
 * Raw-fetch bridge to the new personal-SMTP/POP3 endpoints on GmailController (provider-status,
 * save-smtp-settings, test-smtp-connection, list-inbox-messages, inbox-message, send-smtp-email).
 * These aren't in the generated @propertelligent/client-api yet — mirrors organizationEmailApi.ts's pattern.
 * The existing Gmail endpoints (send/list/disconnect/etc.) already have generated CommunicationService methods
 * and are used directly from emailview.tsx/Compose.tsx/ShowEmail.tsx — not duplicated here.
 */

export interface PersonalEmailStatus {
  isConnected: boolean;
  providerType: EmailProviderType;
  emailAddress: string | null;
  smtpHost: string | null;
  popHost: string | null;
  username: string | null;
  fromName: string | null;
  fromEmail: string | null;
}

export interface PersonalSmtpSettingsRequest {
  smtpHost: string;
  smtpPort: number;
  popHost: string;
  popPort: number;
  username: string;
  password: string;
  useSmtpSsl: boolean;
  usePopSsl: boolean;
  fromName: string;
  fromEmail: string;
}

export interface InboxMessageSummary {
  uid: string;
  from: string | null;
  fromName: string | null;
  fromEmail: string | null;
  subject: string | null;
  displayDate: string | null;
  date: string | null;
}

export interface InboxMessage extends InboxMessageSummary {
  id: string;
  toName: string | null;
  toEmail: string | null;
  body: string | null;
  htmlBody: string | null;
  isImportant: boolean;
  isStarred: boolean;
  attachments?: EmailAttachment[] | null;
}

/**
 * Attachment metadata on a Gmail EmailMessage (GET /api/Gmail/email-thread / /api/Gmail/email-message) --
 * also reused as-is for a POP3 InboxMessage's attachments (GET /api/Gmail/inbox-message), same shape on both.
 * Interim local type -- not yet on the generated @propertelligent/client-api's EmailMessage model (the
 * backend half of bug/email-attachments-outlook adds it in parallel); swap to the generated type once
 * @propertelligent/client-api republishes with this field. Individual fields may be null only in the
 * degenerate case Gmail's own API omits one -- treat defensively wherever this type is consumed.
 */
export interface EmailAttachment {
  attachmentId?: string | null;
  filename?: string | null;
  mimeType?: string | null;
  size?: number | null;
  /**
   * POP3/SMTP only -- base64-encoded raw bytes, present inline because a second POP3 connection to fetch them
   * later isn't reliably possible (see downloadInboxAttachment below). Always null/absent for a Gmail attachment.
   */
  data?: string | null;
}

/**
 * Downloads one Gmail message attachment via the new GET /api/Gmail/attachment endpoint (also not yet on
 * the generated client-api -- same raw-fetch-bridge idiom as every other not-yet-generated endpoint in this
 * file). A plain <a href> to the raw endpoint can't carry the Authorization header, so this fetches the
 * bytes itself and triggers the browser's save behavior via a transient, programmatically-clicked <a
 * download> element pointed at an object URL -- the standard client-side pattern for an
 * authenticated-fetch-then-download. Publishes a Failure notification (matching this module's established
 * PubSub/TopicsEnum.Notification pattern) on a non-ok response or on any thrown error (network failure,
 * blob conversion failure, etc.) -- unlike every other helper in this file (which deliberately throws and
 * leaves feedback to the caller), this one owns its own toast because its caller is a plain row onClick
 * with nowhere else to surface a rejection; swallowing it here, with a toast, is preferable to an unhandled
 * promise rejection from that onClick (bug/email-attachments-outlook review follow-up).
 */
export const downloadGmailAttachment = async (messageId: string, attachment: EmailAttachment): Promise<void> => {
  const params = new URLSearchParams({
    messageId,
    attachmentId: attachment.attachmentId ?? "",
    filename: attachment.filename ?? "attachment",
    mimeType: attachment.mimeType ?? "",
  });
  await downloadAttachmentFromUrl(`${OpenAPI.BASE}/api/Gmail/attachment?${params.toString()}`, attachment.filename);
};

/**
 * Downloads one POP3 inbox-message attachment from its inline base64 `data` -- deliberately NOT a second network
 * fetch like downloadGmailAttachment. A Gmail-backed POP3 account (the common case) stops offering a message in a
 * session's LIST/UIDL the instant it's been RETR'd even once, which already happens the moment the message was
 * opened to view it -- so a later, separate request to re-fetch just this attachment's bytes isn't reliably
 * possible, unlike Gmail's stateless attachments.get. The backend therefore sends the bytes inline on the same
 * response that rendered the message, and this just turns that base64 string into a download, no fetch involved.
 */
export const downloadInboxAttachment = (attachment: EmailAttachment): void => {
  const notifyDownloadFailure = () => {
    PubSub.publish(TopicsEnum.Notification.toString(), {
      type: NotificationType.Failure,
      message: "Unable to download attachment",
    });
  };
  if (!attachment.data) {
    notifyDownloadFailure();
    return;
  }
  try {
    const byteCharacters = atob(attachment.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: attachment.mimeType ?? "application/octet-stream" });
    triggerBlobDownload(blob, attachment.filename);
  } catch (error) {
    console.error("Error decoding inbox attachment:", error);
    notifyDownloadFailure();
  }
};

/** Shared authenticated-fetch-then-download core for downloadGmailAttachment above. */
const downloadAttachmentFromUrl = async (url: string, filename: string | null | undefined): Promise<void> => {
  const notifyDownloadFailure = () => {
    PubSub.publish(TopicsEnum.Notification.toString(), {
      type: NotificationType.Failure,
      message: "Unable to download attachment",
    });
  };
  try {
    const res = await fetch(url, {
      method: "GET",
      headers: authHeaders(),
    });
    if (!res.ok) {
      notifyDownloadFailure();
      return;
    }
    const blob = await res.blob();
    triggerBlobDownload(blob, filename);
  } catch (error) {
    console.error("Error downloading attachment:", error);
    notifyDownloadFailure();
  }
};

/**
 * Shared "save this blob as a file" trigger for both downloadGmailAttachment (fetched) and
 * downloadInboxAttachment (decoded from inline base64) above -- a transient, programmatically-clicked
 * <a download> element pointed at an object URL, the standard client-side pattern for triggering a browser
 * download from in-memory data (no server round trip possible for a plain <a href>, and none needed here either
 * way).
 */
const triggerBlobDownload = (blob: Blob, filename: string | null | undefined): void => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename ?? "attachment";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoking on the next tick (rather than immediately after click()) avoids a real browser footgun -- some
  // engines can cancel an in-flight download if the object URL backing it is revoked too early.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};

/**
 * Formats a UTC ISO date string for display in the viewer's local timezone. The backend's own DisplayDate field
 * is computed server-side against DateTime.UtcNow with no timezone conversion (h:mm tt on the raw UTC value) --
 * fine for a server log, wrong for a user-facing time ("11:50" in Gmail's own UI showed as "6:20 AM" here for an
 * IST user, exactly a 5:30 offset). Recompute it here instead, mirroring the same today/this-year/older shape.
 */
// A backend date-parse fallback (e.g. a Gmail message whose "Date" header couldn't be parsed at all) can still
// surface as the placeholder DateTime.MinValue ("0001-01-01T00:00:00") rather than null/missing -- that's a
// valid, non-NaN JS Date, so it would otherwise render as the equally nonsensical "Jan 1, 1" / "Sun Jan 1, 1
// 12:00 AM". No real email predates the Unix epoch; treat anything older as missing rather than guess at it.
const isPlausibleEmailDate = (date: Date): boolean => date.getFullYear() >= 1971;

export const formatLocalDisplayDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime()) || !isPlausibleEmailDate(date)) return "";

  // Pinned to en-US (not the viewer's OS/browser locale, which Intl's "undefined" locale argument would use)
  // -- this app's dates are consistently MMM d[, yyyy] / American-English everywhere else (see
  // EmailHeaderParsingHelpers.FormatDisplayDate on the backend), and a locale-dependent format here would
  // silently reorder day/month or use 24-hour time on a non-US system.
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/**
 * Full day-name + date + time, in the viewer's local timezone, always (no "today"/"this year" shortening) --
 * matches the Figma design for an opened email's header (e.g. "Mon Jan 11, 2024 11:15 AM"). Used by the opened-
 * message views (ShowEmail.tsx, ShowOutlookMessage.tsx, ShowInboxMessage.tsx) rather than the short, relative
 * formatLocalDisplayDate above, which stays for list rows (bug/email-date-display).
 */
export const formatLocalDisplayDateFull = (isoDate: string | null | undefined): string => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime()) || !isPlausibleEmailDate(date)) return "";

  // Built from separate parts (not a single toLocaleDateString call) so the weekday never gets a
  // locale-inserted comma after it (e.g. en-US's default "Mon, Jan 11, 2024") -- the Figma example has none:
  // "Mon Jan 11, 2024 11:15 AM".
  // Pinned to en-US for the same reason as formatLocalDisplayDate above.
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  const monthDayYear = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${weekday} ${monthDayYear} ${time}`;
};

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem(UserLoginData.token)}`,
});

const jsonHeaders = () => ({
  "Content-Type": "application/json",
  ...authHeaders(),
});

/**
 * Fetch which personal email provider (Gmail or custom SMTP/POP3) the current user has connected, if any.
 * Throws on a non-ok response or a network/parse failure — unlike most helpers in this file, this one is
 * deliberately NOT swallowed to a disconnected-looking fallback, because "the status request failed" and
 * "nothing is connected" need distinct UI (a stale/misleading connect screen vs. a real error state). Callers
 * must catch and render their own error state — see emailview.tsx's `loadProviderStatus`.
 */
export const getProviderStatus = async (): Promise<PersonalEmailStatus> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/provider-status`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return {
    isConnected: !!body?.success?.isConnected,
    providerType: body?.success?.providerType ?? EmailProviderType.None,
    emailAddress: body?.success?.emailAddress ?? null,
    smtpHost: body?.success?.smtpHost ?? null,
    popHost: body?.success?.popHost ?? null,
    username: body?.success?.username ?? null,
    fromName: body?.success?.fromName ?? null,
    fromEmail: body?.success?.fromEmail ?? null,
  };
};

/** Persist the user's custom SMTP/POP3 settings. Throws the non-ok response on failure. */
export const saveSmtpSettings = async (request: PersonalSmtpSettingsRequest): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/save-smtp-settings`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw res;
  }
};

/** Send a live test email using the given (not-yet-saved) settings, without persisting them. */
export const testSmtpConnection = async (request: PersonalSmtpSettingsRequest): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/test-smtp-connection`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw res;
  }
};

/** List the most recent POP3 inbox messages (headers only, no body). */
export const listInboxMessages = async (maxResults = 25): Promise<InboxMessageSummary[]> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/list-inbox-messages?maxResults=${maxResults}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return body?.success ?? [];
};

/** Fetch one full POP3 message (including body) by its stable UID. */
export const getInboxMessage = async (uid: string): Promise<InboxMessage | null> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/inbox-message?uid=${encodeURIComponent(uid)}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return body?.success ?? null;
};

/**
 * Send a new email through the connected personal SMTP account (no reply/forward — see Rule 12 scope note).
 * Body travels as a form-urlencoded POST body, not a URL query string -- the editor now always embeds an
 * inserted/pasted image inline as a base64 data: URI (matching Gmail's own compose behavior), which can run
 * into the hundreds of KB, far past a URL's practical length limit. Matches the backend's [FromForm] binding
 * on send-smtp-email (see GmailController.SendSmtpEmail).
 */
export const sendPersonalEmail = async (to: string, subject: string, body: string): Promise<void> => {
  const params = new URLSearchParams({ To: to, Subject: subject, Body: body });
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/send-smtp-email`, {
    method: "POST",
    headers: authHeaders(),
    body: params,
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Exchange the Microsoft (Azure AD / Outlook) OAuth authorization code for a stored personal
 * connection. Raw-fetch bridge — mirrors saveOutlookRequestToken in organizationEmailApi.ts (org-level)
 * and this file's own saveSmtpSettings for the "not in generated client-api yet" precedent. Rejections
 * propagate so the OAuth-return handler can log them before navigating back.
 */
export const saveOutlookRequestToken = async (code: string, redirectUri: string): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/save-outlook-request-token`, {
    method: "POST",
    headers: jsonHeaders(),
    body: JSON.stringify({
      authorizationToken: code,
      redirectUri,
    }),
  });
  if (!res.ok) {
    throw res;
  }
};

/** Mirrors OutlookMessageProvider.MaxTotalAttachmentBytes (Graph's inline-attachment request limit). */
export const OUTLOOK_MAX_TOTAL_ATTACHMENT_BYTES = 3 * 1024 * 1024;

/**
 * Builds the [FromForm] SendEmailRequest body for the Outlook endpoints: form-urlencoded when there are no
 * attachments (see sendPersonalEmail above for why), multipart only when files need to travel with it.
 */
export const buildOutlookFormBody = (
  fields: Record<string, string>,
  attachments: File[] = []
): URLSearchParams | FormData => {
  if (attachments.length === 0) return new URLSearchParams(fields);
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
  attachments.forEach((file) => formData.append("Attachments", file, file.name));
  return formData;
};

/**
 * Send a new email through the connected personal Outlook account.
 */
export const sendPersonalOutlookEmail = async (
  to: string,
  subject: string,
  body: string,
  attachments: File[] = []
): Promise<void> => {
  const params = buildOutlookFormBody({ To: to, Subject: subject, Body: body }, attachments);
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/send-outlook-email-message`, {
    method: "POST",
    headers: authHeaders(),
    body: params,
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Fetch the connected Outlook account's email address (via Microsoft Graph `/me`), or `null` if not
 * connected / the request fails — mirrors fetchGmailConnectedEmail's swallow-to-null pattern in
 * emailview.tsx since Outlook has no separate profile-status split the way Gmail does.
 */
export const getOutlookProfile = async (): Promise<string | null> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/outlook-profile`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    return null;
  }
  const body = await res.json();
  return body?.success ?? null;
};

/**
 * Phase 2 — personal Outlook inbox parity (list/read/reply/forward/delete/star/counts/contacts), all via
 * Microsoft Graph on the backend (OutlookMessageProvider). These 10 endpoints are brand-new on GmailController
 * and are not in the generated @propertelligent/client-api yet — same raw-fetch bridge precedent as the SMTP/
 * Phase-1-Outlook functions above. Swap to the generated CommunicationService methods once client-api publishes.
 */

export interface OutlookMessageSummary {
  id: string;
  fromName: string | null;
  fromEmail: string | null;
  /** Comma-separated "To" recipient display names / addresses. */
  toName?: string | null;
  toEmail?: string | null;
  subject: string | null;
  displayDate: string | null;
  date: string | null;
  isStarred: boolean;
  isImportant: boolean;
}

export interface OutlookMessage extends OutlookMessageSummary {
  body: string | null;
  htmlBody: string | null;
}

export interface OutlookMessageListResult {
  messages: OutlookMessageSummary[];
  nextPageToken: string | null;
}

export interface OutlookEmailCounts {
  inbox: number;
  sent: number;
  starred: number;
}

export interface Contact {
  name: string | null;
  email: string | null;
  phoneNumber: string | null;
}

/**
 * List the connected Outlook mailbox's messages with real server-side paging (unlike POP3's flat single fetch).
 * `folder` ("Inbox"/"Sent"/"Starred", matching emailview.tsx's `filterOptions` pill titles) is an interim
 * extension beyond the plan's originally documented `query`/`maxResults`/`nextPageToken` params -- without it,
 * the Sent/Starred pills would trigger an identical no-op refetch of the Inbox. Needs reconciliation against
 * the actual backend `list-outlook-messages` implementation once merged (see /implementation-validation).
 */
export const listOutlookMessages = async (
  maxResults: number,
  query = "",
  nextPageToken?: string,
  folder?: string
): Promise<OutlookMessageListResult> => {
  const params = new URLSearchParams({ maxResults: String(maxResults), query });
  if (nextPageToken) params.set("nextPageToken", nextPageToken);
  if (folder) params.set("folder", folder);
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/list-outlook-messages?${params.toString()}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return {
    messages: body?.success?.emailMessages ?? [],
    nextPageToken: body?.success?.nextPageToken ?? null,
  };
};

/** Fetch one full Outlook message (including body) by its Graph message id. */
export const getOutlookMessage = async (emailId: string): Promise<OutlookMessage | null> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/outlook-message?emailId=${encodeURIComponent(emailId)}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return body?.success ?? null;
};

/** Delete an Outlook message by its Graph message id. */
export const deleteOutlookMessage = async (emailId: string): Promise<void> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/delete-outlook-message?emailId=${encodeURIComponent(emailId)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/** Toggle an Outlook message's importance flag. */
export const toggleOutlookMessageImportance = async (emailId: string, isImportant: boolean): Promise<void> => {
  const params = new URLSearchParams({ emailId, isImportant: String(isImportant) });
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/toggle-outlook-message-importance?${params.toString()}`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/** Toggle an Outlook message's starred (Graph "flag") state. */
export const toggleOutlookMessageStarred = async (emailId: string, isStarred: boolean): Promise<void> => {
  const params = new URLSearchParams({ emailId, isStarred: String(isStarred) });
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/toggle-outlook-message-starred?${params.toString()}`, {
    method: "POST",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Reply to an Outlook message. Graph defaults the recipient to the original sender; a non-empty `to`
 * overrides it (needed when replying to one of the user's own sent messages).
 */
export const replyOutlookMessage = async (
  emailId: string,
  body: string,
  to = "",
  attachments: File[] = []
): Promise<void> => {
  const fields: Record<string, string> = { EmailId: emailId, Body: body };
  if (to) fields.To = to;
  const params = buildOutlookFormBody(fields, attachments);
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/reply-outlook-message`, {
    method: "POST",
    headers: authHeaders(),
    body: params,
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Forward an Outlook message to one or more recipients (comma-separated). Body travels as a form-urlencoded
 * POST body -- see sendPersonalEmail above for why.
 */
export const forwardOutlookMessage = async (
  emailId: string,
  to: string,
  body: string,
  attachments: File[] = []
): Promise<void> => {
  const params = buildOutlookFormBody({ EmailId: emailId, To: to, Body: body }, attachments);
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/forward-outlook-message`, {
    method: "POST",
    headers: authHeaders(),
    body: params,
  });
  if (!res.ok) {
    throw res;
  }
};

/**
 * Fetch Outlook mailbox counts. No `important` field — Outlook's importance isn't cheaply countable via
 * Graph (see the UI plan's Open Questions) — the `filterOptions` pills for Outlook are Inbox/Sent/Starred only.
 */
export const getOutlookEmailCounts = async (): Promise<OutlookEmailCounts> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/outlook-email-counts`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return {
    inbox: body?.success?.inbox ?? 0,
    sent: body?.success?.sent ?? 0,
    starred: body?.success?.starred ?? 0,
  };
};

/** Fetch the Outlook account's top contacts (Graph People API). */
export const getOutlookContacts = async (): Promise<Contact[]> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/outlook-contacts`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return body?.success ?? [];
};

/** Search the Outlook account's contacts (Graph People API). */
export const searchOutlookContacts = async (query: string): Promise<Contact[]> => {
  const res = await fetch(`${OpenAPI.BASE}/api/Gmail/outlook-search-contacts?query=${encodeURIComponent(query)}`, {
    method: "GET",
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw res;
  }
  const body = await res.json();
  return body?.success ?? [];
};

/**
 * Fetch the Azure AD application (client) id used to build the Microsoft OAuth consent URL. Raw-fetch
 * bridge (mirrors organizationEmailApi.ts's getMicrosoftClientId) — swap to the generated
 * IntegrationsService method once client-api is regenerated. Resolves to an empty string on failure.
 */
export const getMicrosoftClientId = async (): Promise<string> => {
  try {
    const res = await fetch(`${OpenAPI.BASE}/api/Integrations/keys/microsoft-client-id`, {
      method: "POST",
      headers: authHeaders(),
    });
    if (!res.ok) {
      throw res;
    }
    const body = await res.json();
    return body?.success ?? "";
  } catch (error) {
    console.error("Failed to load Microsoft client id:", error);
    return "";
  }
};
